/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Substitutions = NO -->*/
/*<!-- vale Hightouch.Substitutions = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Git Sync", description = "Version-control supported Hightouch resources in a Git repository.", lastUpdatedDate = new Date(1792540800000), keywords = "version control";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Git Sync",
        description: "Version-control supported Hightouch resources in a Git repository.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Create or choose a Git repository",
          "depth": 1,
          "id": "create-or-choose-a-git-repository"
        }, {
          "value": "Authenticate to Git",
          "depth": 1,
          "id": "authenticate-to-git"
        }, {
          "value": "Configure Git Sync",
          "depth": 1,
          "id": "configure-git-sync"
        }, {
          "value": "Use Git Sync with branch protection",
          "depth": 0,
          "id": "use-git-sync-with-branch-protection"
        }, {
          "value": "Connect staging and production workspaces",
          "depth": 0,
          "id": "connect-staging-and-production-workspaces"
        }, {
          "value": "Use the same branch",
          "depth": 1,
          "id": "use-the-same-branch"
        }, {
          "value": "Use separate branches",
          "depth": 1,
          "id": "use-separate-branches"
        }, {
          "value": "Understand what syncs across environments",
          "depth": 1,
          "id": "understand-what-syncs-across-environments"
        }, {
          "value": "Schemas",
          "depth": 0,
          "id": "schemas"
        }, {
          "value": "Model schema",
          "depth": 1,
          "id": "model-schema"
        }, {
          "value": "Sync schemas",
          "depth": 1,
          "id": "sync-schemas"
        }, {
          "value": "Customer Studio Schema",
          "depth": 1,
          "id": "customer-studio-schema"
        }, {
          "value": "Git Sync limitations for Customer Studio schema",
          "depth": 1,
          "id": "git-sync-limitations-for-customer-studio-schema"
        }, {
          "value": "Event Contract Schema",
          "depth": 1,
          "id": "event-contract-schema"
        }, {
          "value": "Manifests",
          "depth": 1,
          "id": "manifests"
        }, {
          "value": "Aliases",
          "depth": 1,
          "id": "aliases"
        }, {
          "value": "Validate changes",
          "depth": 0,
          "id": "validate-changes"
        }, {
          "value": "Custom YAML validation in CI",
          "depth": 1,
          "id": "custom-yaml-validation-in-ci"
        }, {
          "value": "Review change history",
          "depth": 0,
          "id": "review-change-history"
        }, {
          "value": "Use tunneling for private Git repositories",
          "depth": 0,
          "id": "use-tunneling-for-private-git-repositories"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "What does the Full resync button do?",
          "depth": 1,
          "id": "what-does-the-full-resync-button-do"
        }, {
          "value": "Recreate a deleted YAML file",
          "depth": 1,
          "id": "recreate-a-deleted-yaml-file"
        }, {
          "value": "Protected branch update failed",
          "depth": 1,
          "id": "protected-branch-update-failed"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img",
      a: "a",
      hr: "hr",
      h2: "h2",
      ol: "ol",
      h3: "h3",
      strong: "strong",
      h4: "h4",
      pre: "pre",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {BtOnly, Video, FeatureFlag} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!Video) _missingMdxReference("Video", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Git Sync"
      }), "\n", _jsx(Video, {
        source: "https://cdn.sanity.io/files/pwmfmi47/production/28b600cb8a0e813b85206e5fbd2f44db9ebac380.mp4",
        poster: "https://cdn.sanity.io/images/pwmfmi47/production/268d6fd4a5a5723ed2f0815a1ecdc63988180f71-3024x1700.webp",
        title: "Git Sync and dbt Extensions Overview",
        description: "Learn how Hightouch's Git and dbt extensions work.",
        duration: "4 min",
        uploadDate: "2022-1-31T08:00:00+08:00"
      }), "\n", "\n", _jsx(_components.p, {
        children: "Git Sync connects your Hightouch workspace to a Git repository so you can version-control supported Hightouch resources."
      }), "\n", _jsx(_components.p, {
        children: "With Git Sync, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create and edit multiple Hightouch models and syncs in code"
        }), "\n", _jsx(_components.li, {
          children: "Review changes through Git commits and file diffs"
        }), "\n", _jsx(_components.li, {
          children: "Roll resources back to a previous version"
        }), "\n", _jsx(_components.li, {
          children: "Keep staging and production workspaces aligned through shared YAML files"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Git Sync is bidirectional:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Changes made in the Hightouch UI are written to YAML files in Git. These changes are marked as ", _jsx(_components.code, {
            children: "OUTBOUND"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Changes made to YAML files in Git are applied back to Hightouch. These changes are marked as ", _jsx(_components.code, {
            children: "INBOUND"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each update to a supported resource creates a commit in your configured repository."
      }), "\n", _jsx(_components.p, {
        children: "Git Sync currently supports models (including Customer Studio schema models), syncs, and event contracts (domains, event schemas, and components). Git Sync does not currently support Customer Studio audiences or syncs created from those audiences."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync.png",
          alt: "Git Sync diagram"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Inbound changes made in your Git Sync repository don't require approval from\nanother team member, even if your workspace uses ", _jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "approval\nflows"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Git Sync supports most Git providers, including GitHub, Bitbucket, GitLab, and private ", _jsx(_components.a, {
          href: "#tunneling",
          children: "on-premise"
        }), " repositories. In most cases, changes sync between Hightouch and Git in about one minute."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can connect to Git repositories for two different purposes. Git\nSync uses a Git repository to version-control Hightouch resources, which is\nseparate from Git repositories used to ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "leverage dbt\nmodels"
          }), "."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To set up Git Sync, complete these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create or choose a Git repository."
        }), "\n", _jsx(_components.li, {
          children: "Authenticate Hightouch with your Git provider."
        }), "\n", _jsx(_components.li, {
          children: "Configure Git Sync in Hightouch."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "create-or-choose-a-git-repository",
        children: "Create or choose a Git repository"
      }), "\n", _jsx(_components.p, {
        children: "Use a Git repository to store the YAML files that represent your supported Hightouch resources."
      }), "\n", _jsx(_components.p, {
        children: "If you are creating a new repository, GitHub repositories are the easiest to start with because they can use the Hightouch GitHub app."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If you create an empty repository, make an initial commit, such as adding a\nREADME file. This creates the branch Hightouch needs to connect to."
        })
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-to-git",
        children: "Authenticate to Git"
      }), "\n", _jsx(_components.p, {
        children: "After you have a repository, configure your Git credentials in Hightouch."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/extensions/git-sync/configuration?from=docs",
            children: "Git Sync configuration page"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Configuration > Git credentials"
          }), ", select ", _jsx(_components.strong, {
            children: "Set up"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Choose your Git provider and authentication method."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you use GitHub, we recommend using the ", _jsx(_components.a, {
          href: "https://github.com/apps/hightouch-connect/",
          children: "Hightouch GitHub app"
        }), " for the most up-to-date feature support."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-github-app.png",
          alt: "Selecting the GitHub app"
        })
      }), "\n", _jsx(_components.p, {
        children: "Regardless of provider, grant Hightouch access only to the repository or repositories you want to use with Git Sync."
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-to-a-github-enterprise-repository",
        children: "Authenticate to a GitHub Enterprise repository"
      }), "\n", _jsx(_components.p, {
        children: "If you use GitHub Enterprise with a self-hosted server:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Git service"
          }), ", select ", _jsx(_components.strong, {
            children: "Other"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Username"
          }), ", enter your full GitHub Enterprise username. For example, ", _jsx(_components.code, {
            children: "hightouch123@github.hightouch.com"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Token"
          }), ", enter a ", _jsx(_components.a, {
            href: "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/creating-a-personal-access-token",
            children: "GitHub access token"
          }), " tied to that username."]
        }), "\n", _jsx(_components.li, {
          children: "Make sure the token has access to the repository or repositories Hightouch should use."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-github-enterprise.png",
          alt: "Configuring GitHub Enterprise"
        })
      }), "\n", _jsx(_components.h4, {
        id: "authenticate-to-an-aws-codecommit-repository",
        children: "Authenticate to an AWS CodeCommit repository"
      }), "\n", _jsx(_components.p, {
        children: "If you use AWS CodeCommit, you must use SSH to authenticate."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Configure your AWS CodeCommit repository for SSH by following ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/codecommit/latest/userguide/setting-up-ssh-unixes.html",
            children: "AWS's setup instructions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In Hightouch, select ", _jsx(_components.strong, {
            children: "Other"
          }), " as the ", _jsx(_components.strong, {
            children: "Git service"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SSH"
          }), " as the ", _jsx(_components.strong, {
            children: "Protocol"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-ssh-auth.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "SSH Private key"
          }), ", enter the private key you generated when configuring AWS CodeCommit."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Repository"
          }), ", enter the full SSH URL, including the AWS SSH key ID."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The AWS SSH key ID is different from the general access keys in your AWS account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-ssh-id-1.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-ssh-id-2.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can copy your URL from these ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/codecommit/latest/userguide/how-to-connect.html",
          children: "instructions in AWS's docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-aws-docs.png",
          alt: "Configuring AWS CodeCommit"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configure-git-sync",
        children: "Configure Git Sync"
      }), "\n", _jsx(_components.p, {
        children: "After you authenticate to Git, enable Git Sync for your workspace."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/extensions/git-sync/configuration?from=docs",
            children: "Integrations > Extensions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Version control with Git"
          }), " extension."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, enable ", _jsx(_components.strong, {
            children: "Git Sync"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "extensions/git-sync/git-sync-enabled-toggle.png",
          alt: "Enabling Git Sync on the Configuration tab"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Repository"
          }), " and ", _jsx(_components.strong, {
            children: "Branch"
          }), " Hightouch should read from and write to."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optionally, enter a ", _jsx(_components.strong, {
            children: "Path"
          }), " where Hightouch should create folders for Git Sync files."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you enter a path, Hightouch creates the ", _jsx(_components.code, {
          children: "models"
        }), ", ", _jsx(_components.code, {
          children: "syncs"
        }), ", and other supported folders inside that subfolder. If you don't enter a path, Hightouch saves Git Sync files in the root of your repository."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-repo-synced.png",
          alt: "Models and syncs folders in a Git repository"
        })
      }), "\n", _jsx(_components.p, {
        children: "After you save the configuration, Hightouch creates the configured folder structure if needed. Git Sync then starts syncing supported resources between Hightouch and Git."
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["To start the syncing process immediately, select ", _jsx(_components.strong, {
            children: "Full resync"
          }), "."]
        }), _jsx(_components.p, {
          children: "This pushes all supported workspace resources to Git, then pulls changes from Git back to your workspace. Because this happens in that order, a full resync can overwrite Git changes that have not synced to Hightouch yet."
        }), _jsxs(_components.p, {
          children: ["For more information, see ", _jsx(_components.a, {
            href: "#what-does-the-full-resync-button-do",
            children: "What does the Full resync button do?"
          }), "."]
        })]
      }), "\n", _jsxs(Warning, {
        children: [_jsx(_components.p, {
          children: "Git Sync does not support deletions from Git."
        }), _jsxs(_components.p, {
          children: ["If you delete a YAML file in Git, the corresponding model or sync remains in Hightouch. The file is not recreated in Git until the next change is made in Hightouch or you trigger a ", _jsx(_components.a, {
            href: "#what-does-the-full-resync-button-do",
            children: "full resync"
          }), "."]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-git-sync-with-branch-protection",
        children: "Use Git Sync with branch protection"
      }), "\n", _jsx(_components.p, {
        children: "If your repository uses branch protection, you may need to let the Hightouch Connect app bypass specific branch rules. The Hightouch Connect app can only push to a protected branch if you add it to the bypass list under GitHub rulesets."
      }), "\n", _jsx(_components.p, {
        children: "To configure this in GitHub:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Settings > Rules > Rulesets"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add the Hightouch Connect app to the ", _jsx(_components.strong, {
            children: "Bypass list"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Configure the rest of the ruleset to enforce your branch protection requirements."
        }), "\n"]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["GitHub supports branch protection in both ", _jsx(_components.strong, {
            children: "Settings > Branches"
          }), " and ", _jsx(_components.strong, {
            children: "Settings > Rules > Rulesets"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["The Hightouch Connect app only works with branch protection when you add it to the ", _jsx(_components.strong, {
            children: "Bypass list"
          }), " under ", _jsx(_components.strong, {
            children: "Rulesets"
          }), " and define the rest of your branch protection rules there. It does not work as a force-push app under ", _jsx(_components.strong, {
            children: "Branches"
          }), "."]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "connect-staging-and-production-workspaces",
        children: "Connect staging and production workspaces"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use Git Sync to keep staging and production workspaces aligned through the same Git repository. When multiple workspaces connect to the same repository, ", _jsx(_components.a, {
          href: "#aliases",
          children: "alias files"
        }), " handle environment-specific source and destination IDs."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/select-a-workspace.png",
          alt: "Multiple workspaces in the same organization can connect to the same Git repository"
        })
      }), "\n", _jsx(_components.h3, {
        id: "use-the-same-branch",
        children: "Use the same branch"
      }), "\n", _jsxs(_components.p, {
        children: ["For most teams, the simplest setup is to point both workspaces to the same repository and branch, such as ", _jsx(_components.code, {
          children: "main"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In each workspace, go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/extensions/git-sync/configuration?from=docs",
            children: "Git Sync configuration page"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the same repository and branch."
        }), "\n", _jsx(_components.li, {
          children: "Share model and sync YAML files between both workspaces."
        }), "\n", _jsxs(_components.li, {
          children: ["Create ", _jsx(_components.a, {
            href: "#aliases",
            children: "alias files"
          }), " for each workspace to map shared aliases to the correct source and destination IDs."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, both workspaces can reference ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " in their model files. The staging alias file maps ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " to ", _jsx(_components.code, {
          children: "staging-postgres"
        }), ", while the production alias file maps it to ", _jsx(_components.code, {
          children: "production-postgres"
        }), "."]
      }), "\n", _jsxs(Tip, {
        children: ["For a full staging-to-production example, see ", _jsx(_components.a, {
          href: "#aliases",
          children: "Aliases"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "use-separate-branches",
        children: "Use separate branches"
      }), "\n", _jsx(_components.p, {
        children: "For a more controlled promotion workflow, use a separate branch for each environment."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Point your staging workspace to a development branch, such as ", _jsx(_components.code, {
            children: "develop"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Point your production workspace to your production branch, such as ", _jsx(_components.code, {
            children: "main"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Test and iterate in staging. Changes sync to the development branch."
        }), "\n", _jsx(_components.li, {
          children: "When ready, open a pull request to merge the development branch into the production branch."
        }), "\n", _jsx(_components.li, {
          children: "After the pull request is merged, Git Sync picks up the changes in your production workspace."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "#validate-changes",
          children: "CI checks"
        }), " to validate changes before they reach production."]
      }), "\n", _jsx(_components.h3, {
        id: "understand-what-syncs-across-environments",
        children: "Understand what syncs across environments"
      }), "\n", _jsx(_components.p, {
        children: "Git Sync can version-control and replicate the following resources across environments:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Models"
        }), "\n", _jsx(_components.li, {
          children: "Syncs"
        }), "\n", _jsx(_components.li, {
          children: "Event contracts (domains, event schemas, and components)"
        }), "\n", _jsx(_components.li, {
          children: "Customer Studio schema models, including parent, related, and event models"
        }), "\n", _jsx(_components.li, {
          children: "Customer Studio schema relationships"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Git Sync does not replicate everything. Configure the following manually in each workspace:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Customer Studio traits, audiences, and syncs from audiences"
        }), "\n", _jsx(_components.li, {
          children: "Workspace-level settings, such as approval flows, spaces, and environments"
        }), "\n", _jsx(_components.li, {
          children: "Column-level schema settings configured in the UI, such as redaction, column suggestions, and display configuration"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For Customer Studio schema setup instructions, see ", _jsx(_components.a, {
          href: "/customer-studio/schema-setup",
          children: "Configure Schema Git Sync"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "schemas",
        children: "Schemas"
      }), "\n", _jsx(_components.p, {
        children: "Git Sync represents supported Hightouch resources as YAML files in your Git repository."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Field names in YAML files are case-sensitive. For example, a field named\n", _jsx(_components.code, {
            children: "Email"
          }), " is not the same as ", _jsx(_components.code, {
            children: "email"
          }), ". This applies to all field references,\nincluding source column names, primary keys, and field mappings. If you rename\na column in your warehouse changing only the casing, Git Sync treats it as a\ndifferent field, which can cause Git Sync errors or unexpected behavior."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Schemas created by Identity Graphs, like the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/identity-resolution/golden-record",
            children: "Golden\nRecord"
          }), ", will\n", _jsx(_components.strong, {
            children: "not"
          }), " be displayed in your ./models folder. This is because the model is\nfully managed by Hightouch rather than user-authored."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "model-schema",
        children: "Model schema"
      }), "\n", _jsx(_components.p, {
        children: "Models use the following schema:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model's"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "display"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model's"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "YAML"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "filename"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "source:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "source."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "visible"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "on"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "source"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "page"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "one of:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "raw_sql,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dbt_model"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "dbtModel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dbt"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "select,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "written"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "`model.<package_name>.<model_name>`"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "rawSql:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sql"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "example,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "select"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "albums"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tableName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "isSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "used"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "internally"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "by"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Hightouch,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "should"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "always"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "be"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryKey:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "primary"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "key"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "column"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "example,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "album_id"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "model-field-reference",
        children: "Model field reference"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Valid values"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Any string"
            }), _jsx(_components.td, {
              children: "Display name. Doesn't need to match the YAML filename."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "source"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Source slug"
            }), _jsx(_components.td, {
              children: "Find the slug on the source's settings page in Hightouch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "type"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "raw_sql"
              }), ", ", _jsx(_components.code, {
                children: "table"
              }), ", ", _jsx(_components.code, {
                children: "dbt_model"
              })]
            }), _jsx(_components.td, {
              children: "Determines which of the next three fields is used."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "rawSql"
              })
            }), _jsx(_components.td, {
              children: "Conditional"
            }), _jsx(_components.td, {
              children: "string or null"
            }), _jsx(_components.td, {
              children: "Valid SQL"
            }), _jsxs(_components.td, {
              children: ["Required when ", _jsx(_components.code, {
                children: "type: raw_sql"
              }), ". Set to ", _jsx(_components.code, {
                children: "null"
              }), " otherwise."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "tableName"
              })
            }), _jsx(_components.td, {
              children: "Conditional"
            }), _jsx(_components.td, {
              children: "string or null"
            }), _jsx(_components.td, {
              children: "Table reference"
            }), _jsxs(_components.td, {
              children: ["Required when ", _jsx(_components.code, {
                children: "type: table"
              }), ". Set to ", _jsx(_components.code, {
                children: "null"
              }), " otherwise."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "dbtModel"
              })
            }), _jsx(_components.td, {
              children: "Conditional"
            }), _jsx(_components.td, {
              children: "string or null"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model.<package>.<name>"
              })
            }), _jsxs(_components.td, {
              children: ["Required when ", _jsx(_components.code, {
                children: "type: dbt_model"
              }), ". Set to ", _jsx(_components.code, {
                children: "null"
              }), " otherwise."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "isSchema"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "boolean"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "false"
              })
            }), _jsxs(_components.td, {
              children: ["Always set to ", _jsx(_components.code, {
                children: "false"
              }), " for standard models."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "primaryKey"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Column name"
            }), _jsx(_components.td, {
              children: "Must match an existing column in the model's query results."
            })]
          })]
        })]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["A model's slug is its YAML filename. For example, ", _jsx(_components.code, {
            children: "ab-01-a.yaml"
          }), " has the slug\n", _jsx(_components.code, {
            children: "ab-01-a"
          }), ". Syncs and relationships refer to models by slug, not by ", _jsx(_components.code, {
            children: "name"
          }), "."]
        }), _jsx("br", {}), _jsxs(_components.p, {
          children: ["To update an existing model, keep the same filename. If you rename the file,\nGit Sync creates a new model instead of updating the existing one. You can\nchange ", _jsx(_components.code, {
            children: "name"
          }), " without affecting the slug."]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "sync-schemas",
        children: "Sync schemas"
      }), "\n", _jsx(_components.p, {
        children: "Sync schemas vary by destination. Because each destination has different configuration options, we recommend creating syncs in the Hightouch UI first. You can then use the generated YAML file as a starting point for updates or new syncs. The YAML filename is the sync name."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "model:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "model"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(its"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "YAML"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "filename,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "without"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ".yaml)"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destination:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "destination"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name/slug"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "config:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "destination"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "dependent"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "configuration"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync."
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schedule:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "type"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "schedule"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "use."
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "format"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "here"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "varies"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "based"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "on"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-string",
            children: "schedule"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "selected,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "we"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "recommend"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "using"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "UI"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "as"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "a"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "starting"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "point."
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schedulePaused:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "boolean,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "where"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "sync"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "paused,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "enabled."
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-field-reference",
        children: "Sync field reference"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Valid values"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Model slug"
            }), _jsxs(_components.td, {
              children: ["Must match the filename of an existing model YAML file, without ", _jsx(_components.code, {
                children: ".yaml"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "destination"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Destination slug"
            }), _jsxs(_components.td, {
              children: ["Find the slug on the destination's settings page or in the ", _jsx(_components.a, {
                href: "#manifests",
                children: "manifest file"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "config"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "object"
            }), _jsx(_components.td, {
              children: "Destination-specific"
            }), _jsx(_components.td, {
              children: "Structure varies by destination. Create a sync in the UI first to see the generated config shape."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "schedule"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "object"
            }), _jsx(_components.td, {
              children: "Schedule object"
            }), _jsx(_components.td, {
              children: "Format depends on schedule type (interval, cron, etc.). Use a UI-created sync as a reference."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "schedulePaused"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "boolean"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "true"
              }), ", ", _jsx(_components.code, {
                children: "false"
              })]
            }), _jsxs(_components.td, {
              children: ["Controls whether the sync is ", _jsx(_components.a, {
                href: "/syncs/overview#individual-sync-overview-page",
                children: "enabled or disabled"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The safest way to learn the ", _jsx(_components.code, {
            children: "config"
          }), " structure for a destination is to create\na sync manually in the Hightouch UI, then inspect the generated YAML file in\nyour Git repository. Use that file as a template for additional syncs to the\nsame destination."]
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Avoid modifying YAML files while a sync is actively running. Pause the sync in\nthe Hightouch UI before committing structural changes to prevent unexpected\nbehavior."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "customer-studio-schema",
        children: "Customer Studio Schema"
      }), "\n", _jsxs(_components.p, {
        children: ["Git Sync supports version-controlling your Customer Studio schema—including parent models, related models, event models, and relationships. For full setup instructions, see ", _jsx(_components.a, {
          href: "/customer-studio/schema-setup",
          children: "Configure Schema Git Sync"
        }), "."]
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Git Sync for Customer Studio schema",
        contactMessage: "Hi, I'm interested in Git Sync for Customer Studio schema."
      }), "\n", _jsx(_components.h4, {
        id: "parent-model-schema",
        children: "Parent model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#create-the-parent-model",
          children: "Parent models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/parent-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), ", with the following additions:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryLabel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"primary_label_column\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Optional"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "secondaryLabel:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"secondary_label_column\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Optional"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "related-model-schema",
        children: "Related model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#add-a-related-model",
          children: "Related models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/related-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "event-model-schema",
        children: "Event model schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#add-an-event-model",
          children: "Event models"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/event-models"
        }), " directory. They have the same schema as ", _jsx(_components.a, {
          href: "#model-schema",
          children: "regular models"
        }), ", with the following addition:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "eventTimestampColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"column_name\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Required"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h4, {
        id: "model-relationship-schemas",
        children: "Model relationship schemas"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/schema#understand-how-relationships-work",
          children: "Model relationships"
        }), " live in the ", _jsx(_components.code, {
          children: "schema/model-relationships"
        }), " directory."]
      }), "\n", _jsx(_components.p, {
        children: "The schema for model relationships depends on whether the relationship is direct or through other relationships."
      }), "\n", _jsx(_components.p, {
        children: "Direct relationships look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "from:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "relationshipName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Relationship name for from -> to"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mergeColumns:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# or true"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "to:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "relationshipName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Relationship name for to -> from"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mergeColumns:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "cardinality:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1:1\""
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# One of: \"1:1\", \"1:many\", \"many:1\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "mapping:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "fromColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "toColumn:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Through relationships look like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"relationship name\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "from:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "to:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "slug:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"model_slug\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "through:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# List of relationship slugs that constitute this through relationship."
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "relationship_slug_1"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-bullet",
            children: "-"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "relationship_slug_2"
          }), "\n"]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "git-sync-limitations-for-customer-studio-schema",
        children: "Git Sync limitations for Customer Studio schema"
      }), "\n", _jsx(_components.p, {
        children: "Git Sync currently supports version-controlling model definitions and relationship structure. However, certain schema configurations set in the Hightouch UI are not synced across environments."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, Git Sync does ", _jsx(_components.strong, {
          children: "not"
        }), " copy:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Column-level settings (for example, redaction, column suggestions, or display configuration set in the UI)"
        }), "\n", _jsx(_components.li, {
          children: "Column aliases"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The relationship-level ", _jsx(_components.code, {
          children: "mergeColumns"
        }), " setting is synced, because it's part of the relationship YAML."]
      }), "\n", _jsx(_components.p, {
        children: "If you migrate a schema to another environment using Git Sync, you may need to reconfigure these settings manually after deployment."
      }), "\n", _jsxs(_components.p, {
        children: ["Field names are also case-sensitive in YAML. If a column name changes only in casing (for example, ", _jsx(_components.code, {
          children: "user_id"
        }), " to ", _jsx(_components.code, {
          children: "User_Id"
        }), "), Git Sync treats these as different fields. Make sure column references in your YAML files match the exact casing used in your warehouse."]
      }), "\n", _jsxs(_components.p, {
        children: ["→ For full setup instructions, see ", _jsx(_components.a, {
          href: "/customer-studio/schema-setup",
          children: "Configure Schema Git Sync"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "event-contract-schema",
        children: "Event Contract Schema"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management",
          children: "Event contracts"
        }), " are synced as domains, event schemas, and components. The ", _jsx(_components.a, {
          href: "/events/contracts/cli",
          children: "Events CLI"
        }), " can generate typed SDK wrappers from this checkout instead of calling the API. A repository uses exactly one of two on-disk layouts:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Legacy: ", _jsx(_components.code, {
            children: "events/contracts/"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Current: ", _jsx(_components.code, {
            children: "events/domains/"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Don't mix both layouts in the same repository. For new workspaces, use the domains layout."
      }), "\n", _jsx(_components.h4, {
        id: "domains",
        children: "Domains"
      }), "\n", _jsx(_components.p, {
        children: "Domain metadata has a simple schema:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "domain"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "description:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "description"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "domain"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default)"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "—"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "legacy"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "layout"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "only"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "list"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event-source"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slugs"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "domain"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "is"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "attached"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "File locations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Legacy: ", _jsx(_components.code, {
            children: "events/contracts/<domain-slug>.yaml"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Current: ", _jsx(_components.code, {
            children: "events/domains/<domain-slug>/domain.yaml"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In the current layout, ", _jsx(_components.code, {
          children: "onUndeclaredSchema"
        }), " is omitted. Undeclared-event enforcement is configured per ", _jsx(_components.a, {
          href: "/events/contracts/violations#violation-enforcement",
          children: "event source"
        }), ", not in Git Sync YAML."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "sources"
        }), " uses ", _jsx(_components.a, {
          href: "/events/sources/overview",
          children: "event source"
        }), " slugs (the slug on the source's settings page), not write keys or UUIDs. Inbound treats a missing ", _jsx(_components.code, {
          children: "sources"
        }), " key as \"leave membership alone\", so YAML written before this field existed will not detach sources. An empty list (", _jsx(_components.code, {
          children: "sources: []"
        }), ") detaches every source from the domain. A non-empty list is a replace-set."]
      }), "\n", _jsx(_components.h4, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management#add-an-event-contract",
          children: "Event Schemas"
        }), " are YAML files that define the allowed format of events sent to Hightouch.\nEach file represents a single version of a single event within a domain."]
      }), "\n", _jsx(_components.p, {
        children: "File locations:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Legacy: ", _jsx(_components.code, {
            children: "events/contracts/<domain-slug>/<event-slug>.yaml"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Current: ", _jsx(_components.code, {
            children: "events/domains/<domain-slug>/<event-slug>.yaml"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The filename is the slug of the event, which is derived from the name or type, the domain slug, and the version. Don't use the reserved slug ", _jsx(_components.code, {
          children: "domain"
        }), " in the current layout — it would collide with ", _jsx(_components.code, {
          children: "domain.yaml"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The schema for an event is as follows:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"identify\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"group\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"page\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"screen\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"alias\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "track"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "events,"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name;"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "otherwise"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "should"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "not"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "be"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "present"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "version"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event;"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "defaults"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"default\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onSchemaViolation:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default)"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredFields:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"ALLOW_EVENT\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default),"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"BLOCK_EVENT\""
          }), _jsx(_components.span, {
            className: "hljs-string",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"OMIT_FIELDS\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "includeBuiltInContext:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "one"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"true\""
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "(default),"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "or"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"false\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "|\n  Schema object for validating the event, adhering to the JSON Schema spec\n  Usually the root of this schema is an object which contains a nested object named properties\n"
          })]
        })
      }), "\n", _jsx(_components.p, {
        children: "In the following example, we have a Sample Event that contains a single optional \"id\" field:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# events/domains/web/sample-event-web-default.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Sample"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Event"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onSchemaViolation:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "ALLOW_EVENT"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "onUndeclaredFields:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "ALLOW_EVENT"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schema:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "object"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# The root of our schema is an object"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Objects contain a \"properties\" element in the JSON Schema spec"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# We define a single child named properties"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "object"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is an object"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Finally, the properties our child object contains"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "id:"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is only an optional id field"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "string"
          }), " ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Which is of type string"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "track"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "default"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "This corresponds to the following JSON in the in-app JSON Editor:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n          ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"string\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Because event schemas may get complex, using Hightouch ", _jsx(_components.a, {
          href: "#validate-changes",
          children: "CI checks"
        }), " is a great way to validate your changes\nbefore merging them into your repository."]
      }), "\n", _jsx(_components.h4, {
        id: "components",
        children: "Components"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management#add-a-component",
          children: "Components"
        }), " are reusable schema pieces. They're located at ", _jsx(_components.code, {
          children: "events/domains/<domain-slug>/components/<component-slug>.yaml"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "name"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "component"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "version"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "component;"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "defaults"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"default\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "description:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "description"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "for"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "the"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "component"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: ">"
          }), " ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "optional:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "list"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "of"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "event-source"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "slugs"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "this"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "component"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "applies"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "to"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "schema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "|\n  Schema object for the component, adhering to the JSON Schema spec.\n  Imports of other components are represented as $ref values inside this schema.\n"
          })]
        })
      }), "\n", _jsx(_components.p, {
        children: "The domain slug is derived from the file path and shouldn't appear in the YAML body."
      }), "\n", _jsxs(_components.p, {
        children: ["Component ", _jsx(_components.code, {
          children: "sources"
        }), " uses the same event-source slugs as domain membership. A missing key leaves the component's source whitelist unchanged. An empty list (", _jsx(_components.code, {
          children: "sources: []"
        }), ") means the component applies to all sources (no whitelist). A non-empty list is a whitelist: the component is omitted from source-scoped reads (for example ", _jsx(_components.code, {
          children: "GET /events/domains?source="
        }), ") for any other source, and ", _jsx(_components.code, {
          children: "$ref"
        }), "s to those omitted components are ignored in the returned event schemas."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "manifests",
        children: "Manifests"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch creates a manifest file at the root of your Git repository for each connected workspace. ", _jsx(_components.strong, {
          children: "Manifest files"
        }), " list all sources and destinations in a workspace, making it easier for you to find their IDs to add to sync and model files or reference in ", _jsx(_components.a, {
          href: "#aliases",
          children: "aliases"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Manifest filenames follow this format: ", _jsx(_components.code, {
          children: "manifest-<your-workspace-id>.yaml"
        }), ". A manifest file follows this format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# READ ONLY FILE"
          }), "\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Changes made here will be ignored. Please use the Hightouch dashboard to create and edit sources and destinations."
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "production-postgres:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Production"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "postgres"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "postgres"
          }), "\n   ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "snowflake-243-tenant:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Snowflake"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "EU"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "instance"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "snowflake"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destinations:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "production-salesforce:"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Production"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Salesforce"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "salesforce"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Sources and destinations are listed by their ID. In the example file above, the Postgres source's ID is ", _jsx(_components.code, {
          children: "production-postgres"
        }), " and the Salesforce destination's ID is ", _jsx(_components.code, {
          children: "production-salesforce"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Manifest files are ", _jsx(_components.strong, {
            children: "read-only"
          }), ". Git Sync overwrites any changes made to\nmanifest files. Use the Hightouch dashboard to create and edit sources and\ndestinations."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "aliases",
        children: "Aliases"
      }), "\n", _jsxs(_components.p, {
        children: ["Managing multiple Hightouch workspaces from the same Git repository can be difficult because source and destination IDs can differ between workspaces. ", _jsx(_components.strong, {
          children: "Alias files"
        }), " let you create custom IDs for sources and destinations in a given workspace allowing model and sync files to work across different workspaces."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch creates an empty alias file in your Git repository for each connected workspace. Alias filenames follow this format: ", _jsx(_components.code, {
          children: "aliases-<your-workspace-id>.yaml"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can then add sources and destinations to the alias file following this format:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# e.g. aliases-hightouch-production-workspace.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "my-source-alias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "my-actual-source-id"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "othersourcealias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "another-workspace-source"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "destinations:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "my-destination-alias:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "existing-destination"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Each key is an alias and each value should be an actual source or destination ID. You can find source and destination IDs in the ", _jsx(_components.a, {
          href: "#manifests",
          children: "manifest file"
        }), " in the root of your Git repository."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Aliases should be unique across sources and destinations in the same\nworkspace. In other words, you can't use the same alias for multiple resources\nin the same alias file."
        })
      }), "\n", _jsx(_components.h4, {
        id: "example-alias-usage",
        children: "Example alias usage"
      }), "\n", _jsxs(_components.p, {
        children: ["Consider a workspace, ", _jsx(_components.code, {
          children: "acme-staging"
        }), ", with a model ", _jsx(_components.code, {
          children: "stocking-list.yaml"
        }), " file like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-attr",
            children: "name:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "Stocking"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "list"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "source:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "staging-postgres"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "table"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "tableName:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "public.workspaces"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "isSchema:"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "false"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "primaryKey:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "id"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Another workspace called ", _jsx(_components.code, {
          children: "acme-production"
        }), " is connected to the same repository but can't use this model during Git Sync. That's because the ", _jsx(_components.code, {
          children: "acme-production"
        }), " workspace doesn't have a source with the ID ", _jsx(_components.code, {
          children: "staging-postgres"
        }), ". Instead, ", _jsx(_components.code, {
          children: "acme-production"
        }), " uses a Postgres source with the ID ", _jsx(_components.code, {
          children: "production-postgres"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can use aliases so that both workspaces can use the same model ", _jsx(_components.code, {
          children: "stocking-list.yaml"
        }), " file."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add an alias to the ", _jsx(_components.code, {
            children: "acme-staging"
          }), " workspace alias file:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-acme-staging.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "aliased-postgres:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "staging-postgres"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This creates the custom ID ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " that points to the source with the ID ", _jsx(_components.code, {
          children: "staging-postgres"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Add an alias to the ", _jsx(_components.code, {
            children: "acme-production"
          }), " workspace alias file:"]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-yaml",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "# aliases-acme-production.yaml"
          }), "\n", _jsx(_components.span, {
            className: "hljs-attr",
            children: "sources:"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "aliased-postgres:"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "production-postgres"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This creates the custom ID ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " that points to the source with the ID ", _jsx(_components.code, {
          children: "production-postgres"
        }), ". For the next step, it's important that both alias files use the same alias name. Since these aliases exist in different workspaces, they are still unique within their respective workspaces."]
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Update the ", _jsx(_components.code, {
            children: "source"
          }), " value of ", _jsx(_components.code, {
            children: "stocking-list.yaml"
          }), " to ", _jsx(_components.code, {
            children: "aliased-postgres"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-diff",
          children: ["name: Stocking list\n", _jsx(_components.span, {
            className: "hljs-deletion",
            children: "- source: staging-postgres"
          }), "\n", _jsx(_components.span, {
            className: "hljs-addition",
            children: "+ source: aliased-postgres"
          }), "\ntype: table\ntableName: public.workspaces\nisSchema: false\nprimaryKey: id\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Now, during Git Sync, both workspaces resolve ", _jsx(_components.code, {
          children: "aliased-postgres"
        }), " to their respective Postgres slugs and Git Sync completes for both staging and production workspaces."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "validate-changes",
        children: "Validate changes"
      }), "\n", _jsx(_components.p, {
        children: "You can use Hightouch CI checks to validate changes in your Git Sync repository before merging them."
      }), "\n", _jsxs(_components.p, {
        children: ["To enable CI checks, turn on ", _jsx(_components.strong, {
          children: "GitHub Pull Request checks"
        }), " in your Git Sync ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/git-sync/configuration?from=docs",
          children: "configuration page"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "After CI checks are enabled, Hightouch runs a check on each pull request made to the connected Git repository. If a breaking change is detected, such as deleting a model used by a sync, the CI check fails. Open the failed check details to see which sync is affected."
      }), "\n", _jsx(_components.p, {
        children: "If the pull request does not affect any Hightouch syncs, the CI check passes."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/passed-code-check.png",
          alt: "Passed CI check"
        })
      }), "\n", _jsx(_components.h3, {
        id: "custom-yaml-validation-in-ci",
        children: "Custom YAML validation in CI"
      }), "\n", _jsx(_components.p, {
        children: "In addition to Hightouch's built-in CI checks, you can add your own YAML validation step to your CI pipeline to catch syntax errors and missing required fields before a pull request is merged."
      }), "\n", _jsxs(_components.p, {
        children: ["Here's an example using a simple validation script with ", _jsx(_components.code, {
          children: "yq"
        }), " (a YAML processor available in most CI environments):"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: [_jsx(_components.span, {
            className: "hljs-meta",
            children: "#!/bin/bash"
          }), "\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# validate-git-sync-yaml.sh"
          }), "\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Validates model and sync YAML files for required fields."
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "set"
          }), " -euo pipefail\n\nERRORS=0\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Validate model files"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " file ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " models/*.yaml; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), "\n  [ -f ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), "\""]
          }), " ] || ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "continue"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " field ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " name ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "source"
          }), " ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "type"
          }), " primaryKey; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ! yq -e ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\".", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$field"
            }), "\""]
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), "\""]
          }), " > /dev/null 2>&1; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "then"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "echo"
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"ERROR: ", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), " is missing required field '", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$field"
            }), "'\""]
          }), "\n      ERRORS=$((ERRORS + ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "))\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "fi"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "done"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "done"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "# Validate sync files"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " file ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " syncs/*.yaml; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), "\n  [ -f ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), "\""]
          }), " ] || ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "continue"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "for"
          }), " field ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " model destination config schedule schedulePaused; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "do"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " ! yq -e ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\".", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$field"
            }), "\""]
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), "\""]
          }), " > /dev/null 2>&1; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "then"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "echo"
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"ERROR: ", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$file"
            }), " is missing required field '", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$field"
            }), "'\""]
          }), "\n      ERRORS=$((ERRORS + ", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "))\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "fi"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "done"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "done"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "if"
          }), " [ ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$ERRORS"
            }), "\""]
          }), " -gt 0 ]; ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "then"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "echo"
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["\"", _jsx(_components.span, {
              className: "hljs-variable",
              children: "$ERRORS"
            }), " validation error(s) found.\""]
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "exit"
          }), " 1\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "fi"
          }), "\n\n", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "echo"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"All YAML files passed validation.\""
          }), "\n"]
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "This script checks for required fields only. Hightouch's built-in CI checks\nhandle deeper validation, such as verifying that referenced models and\ndestinations exist. Use both for the most coverage."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-change-history",
        children: "Review change history"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Git Sync gives you a changelog through commit history. You can also use ", _jsx(_components.a, {
            href: "/workspace-management/audit-logs",
            children: "audit\nlogs"
          }), " to review changes in Hightouch."]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can use your Git repository to see who created or modified supported resources and when."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "To show the correct user for Hightouch-created commits, the user's Hightouch\nemail must match their GitHub email."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In GitHub, open the repository's ", _jsx(_components.strong, {
          children: "Commits"
        }), " tab or file history to review changes."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-audit-log.png",
          alt: "Commit logs in GitHub"
        })
      }), "\n", _jsx(_components.p, {
        children: "Open a commit to see the exact line-by-line diff."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-audit-log-detail.png",
          alt: "File diff in GitHub"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-tunneling-for-private-git-repositories",
        children: "Use tunneling for private Git repositories"
      }), "\n", _jsxs(_components.p, {
        children: ["If your Git repository is hosted on an on-premise server without public internet access, use ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling"
        }), " to connect Hightouch to your Git server."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/tunnels?from=docs",
            children: [_jsx(_components.strong, {
              children: "Tunnels"
            }), " tab"]
          }), " on the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create a tunnel or reverse tunnel that connects to your Git server. For more information, see the ", _jsx(_components.a, {
            href: "/security/ssh-tunneling",
            children: "tunneling documentation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/extensions/git-sync/configuration?from=docs",
            children: "Git Sync configuration page"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Configuration > Git credentials"
          }), ", select ", _jsx(_components.strong, {
            children: "Manage"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Add your Git credential and select the correct tunnel."
        }), "\n"]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["We recommend using the SSH protocol and ", _jsx(_components.code, {
            children: "ssh_privatekey"
          }), " to connect to private Git repositories."]
        }), _jsxs(_components.p, {
          children: ["Basic authentication can have issues forwarding authentication headers through an SSH tunnel and HTTP redirects. For more information about SSH authentication, see the ", _jsx(_components.a, {
            href: "https://docs.github.com/en/authentication/connecting-to-github-with-ssh",
            children: "GitHub SSH documentation"
          }), "."]
        }), _jsxs(_components.p, {
          children: ["Hightouch does not support ", _jsx(_components.code, {
            children: "ssh_privatekey"
          }), " with passphrases."]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-repo-ssh-tunnel.png",
          alt: "Entering Git credentials in the Hightouch UI"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsx(_components.p, {
        children: "After Git Sync is configured, you can use Git workflows to manage supported Hightouch resources. For example, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create a new model by copying an existing model file and updating the name and query"
        }), "\n", _jsx(_components.li, {
          children: "Create a new model and sync in the same pull request"
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "#validate-changes",
            children: "CI checks"
          }), " to validate changes before merging them"]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.a, {
            href: "#aliases",
            children: "aliases"
          }), " to share model and sync files across staging and production workspaces"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "what-does-the-full-resync-button-do",
        children: "What does the Full resync button do?"
      }), "\n", _jsxs(_components.p, {
        children: ["Selecting ", _jsx(_components.strong, {
          children: "Full resync"
        }), " runs an outbound sync for all supported resources in the workspace. This pushes all supported resources from Hightouch to Git, then pulls changes from Git back to your workspace."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/git-sync-outbound.png",
          alt: "Git Sync diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If your Git repository has changes that have not synced to Hightouch yet, ", _jsx(_components.strong, {
          children: "Full resync"
        }), " can overwrite those changes."]
      }), "\n", "\n", _jsx(_components.h3, {
        id: "recreate-a-deleted-yaml-file",
        children: "Recreate a deleted YAML file"
      }), "\n", _jsxs(_components.p, {
        children: ["Git Sync does not support deletions from Git. If you delete a YAML file in Git, the corresponding model or sync remains in Hightouch. The file is not recreated in Git until the next change is made in Hightouch or you trigger a ", _jsx(_components.a, {
          href: "#what-does-the-full-resync-button-do",
          children: "full resync"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To recreate a deleted YAML file, select ", _jsx(_components.strong, {
          children: "Full resync"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also recreate the file manually by adding a YAML file with the same filename and ", _jsx(_components.a, {
          href: "#schemas",
          children: "schema"
        }), " as the original file. Hightouch recognizes that the file refers to an existing model or sync and associates it with the existing resource."]
      }), "\n", _jsx(_components.h3, {
        id: "protected-branch-update-failed",
        children: "Protected branch update failed"
      }), "\n", _jsx(_components.p, {
        children: "If you selected a protected branch, Git Sync setup might fail with this error:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Protected branch update failed - Changes must be made through ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "a"
          }), " pull request\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "To resolve the error, use one of these options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Select a branch that is not protected, then open pull requests in GitHub to merge updates into the protected branch."
        }), "\n", _jsxs(_components.li, {
          children: ["Follow the instructions in ", _jsx(_components.a, {
            href: "#use-git-sync-with-branch-protection",
            children: "Use Git Sync with branch protection"
          }), " to let the Hightouch Connect app work with your protected branch."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
